'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import useCommonStore from '@/store/commonStore'
import { usePreventScroll } from '@/hooks/usePreventScroll'
import Image from 'next/image'


interface FilterModalProps {
  open: boolean
  onClose: () => void

}

export function FilterModal({
  open,
  onClose,

}: FilterModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const [activeTab, setActiveTab] = useState(0)
  const [localSelected, setLocalSelected] = useState<Record<string, string[]>>({})
  const { filterData } = useCommonStore()
  const router = useRouter()
  
  usePreventScroll(open)
  
  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, onClose])


  // Reset local state when modal opens
  const searchParams = useSearchParams()
  useEffect(() => {
    if (open){
      const initialSelected: Record<string, string[]> = {}
      // Iterate all search params and pick those starting with f_
      searchParams.forEach((value, key) => {
        if (key.startsWith('f_')) {
          const filterKey = key.slice(2)
          initialSelected[filterKey] = value.split('-')
        }
      })
      setLocalSelected(initialSelected)
      
    }
  }, [open,])

  if (!open) return null

  const currentFilter = filterData[activeTab]

  const handleOptionChange = (filterLabel: string, value: string) => {
    setLocalSelected((prev) => {
      const prevArr = prev[filterLabel] || []
      if (prevArr.includes(value)) {
        return { ...prev, [filterLabel]: prevArr.filter((v) => v !== value) }
      }
      return { ...prev, [filterLabel]: [...prevArr, value] }
    })
  }

  const handleReset = () => {
    setLocalSelected({})
    const sp = new URLSearchParams()
    searchParams.forEach((value, key) => {
      if (key.startsWith('f_')) {
        sp.delete(key)
      }else{
        sp.set(key,value)
      }
    })
    router.replace(`?${sp.toString()}`)
    onClose()
  }

  const handleApply = () => {
    const sp = new URLSearchParams()
    searchParams.forEach((v, k) => {
      if (!k.startsWith('f_')) {
        sp.set(k, v)
      }
    })
    Object.keys(localSelected).forEach((key) => {
      if (localSelected[key]?.length > 0) {
        sp.set(`f_${key}`, localSelected[key].join('-'))
      }
    })
    router.replace(`?${sp.toString()}`)
    onClose()
  }

  return (
    <div className="fixed inset-0 w-full h-screen z-50 flex items-end justify-center bg-black/30 transition-colors">
      <div
        ref={modalRef}
        className="w-full h-screen max-w-2xl bg-white flex flex-col animate-slideDown"
      >
        {/* Header */}
        <div className="flex  items-center gap-2 px-4 py-4 border-b">
          <button onClick={onClose} className="p-1 -ml-2">
            <Image src={"/back-arrow.svg"} alt="back" width={18} height={18} />
          </button>
          <span className="font-medium text-sm">
            Filters
          </span>
        </div>
        {/* Content */}
        <div className="flex flex-1 min-h-0 ">
          {/* Tabs */}
          <div className="w-40 bg-[#f6f8fa] py-4 px-2 flex flex-col gap-1 overflow-y-auto">
            {filterData.map((filter, idx) => (
              <button
                key={filter.key+idx}
                className={`text-start px-3 py-2 rounded font-medium text-sm ${
                  idx === activeTab
                    ? 'bg-white text-primary'
                    : 'text-gray-700'
                }`}
                onClick={() => setActiveTab(idx)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {/* Options */}
          <div className="flex-1 px-3 py-3 overflow-y-auto">
            <div className="font-semibold text-sm mb-4">
              {currentFilter.label}
            </div>
            {currentFilter.items.length === 0 ? (
              <div className="text-gray-400 text-sm">No options</div>
            ) : (
              <div className="flex flex-col gap-3">
                {currentFilter.items.map((option:{value:string,label:string}) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={
                        (localSelected[currentFilter.key] || []).includes(option.value)
                      }
                      onChange={() =>
                        handleOptionChange(currentFilter.key, option.value)
                      }
                      className="w-4 h-4 accent-primary"
                    />
                    <span>
                      {option.label}{' '}
                      {/* <span className="text-gray-400 text-xs">
                        ({option.count})
                      </span> */}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Footer */}
        <div className="flex gap-4 px-4 py-4 border-t bg-white fixed bottom-0 w-full max-w-2xl m-x-auto">
          <button
            className="flex-1 py-3 border border-black rounded-lg text-black font-medium bg-white"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            className="flex-1 py-3 rounded-lg text-white font-medium bg-black"
            onClick={handleApply}
          >
            Apply Filter
          </button>
        </div>
      </div>
      {/* <style jsx global>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s cubic-bezier(0.4,0,0.2,1);
        }
      `}</style> */}
    </div>
  )
}